// utils.js

// ---------- FORMATTING UTILITIES ----------

export function formatCash(value) {
  if (!value) return '0,00';

  const number = parseFloat(value);
  if (isNaN(number)) return '0,00';

  const fixed = number.toFixed(2);
  let [integerPart, decimalPart] = fixed.split('.');

  // Add space as thousands separator
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${integerPart},${decimalPart}`;
};

export function formatDate(dateString) {
  if (!dateString) return 'DD/MM/YYYY';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) throw new Error("Invalid Date");

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Date formatting error:', error);

    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();

    return `${day}/${month}/${year}`;
  }
}

export function formatDateToMonthYear(dateString) {
  if (!dateString) return 'Month YYYY';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) throw new Error('Invalid Date');

    const options = { year: 'numeric', month: 'long' };
    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Month-Year formatting error:', error);

    const today = new Date();
    const options = { year: 'numeric', month: 'long' };
    return today.toLocaleString('en-US', options);
  }
}

// ---------- FINANCIAL CALCULATIONS ----------

export function calcProfessionalFee(billables) {
  if (!Array.isArray(billables)) return 0;

  return billables.reduce((total, item) => {
    return total + (item.unitPrice || 0);
  }, 0);
}

export function findSelectedProfessional(items) {
  if (!Array.isArray(items)) return null;

  return items.find(item => item.status === "SELECTED") || null;
}

export function findQuoteByProfessional(quotes, selectedProfessionalId) {
  if (!Array.isArray(quotes) || !selectedProfessionalId) return null;

  return quotes.find(quote => quote.professional === selectedProfessionalId) || null;
}

export function calcTotalWithTax(amount, taxRate = 0.10) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcTotalWithTax:", amount);
    return formatCash(0);
  }

  const total = amount + (amount * taxRate);
  return total;
}

export function calcPlatformFee(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcProfessionalFee:", amount);
    return formatCash(0);
  }

  return amount * 0.10;
}

export function calcClientPlatformFee(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcClientPlatformFee:", amount);
    return formatCash(0);
  }

  return amount * 0.15; // Client fee (same % for now)
}

export function calcSponsorServiceFee(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcSponsorServiceFee:", amount);
    return formatCash(0);
  }

  return amount * 0.15; // Sponsor fee (same % for now)
}

export function calcSponsorVat(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcSponsorVat:", amount);
    return formatCash(0);
  }

  const professionalFee = amount;
  const sponsorFee = calcSponsorServiceFee(amount); // 15% of amount
  const vat = 0.15 * (professionalFee + sponsorFee);

  return vat;
}

export function calcClientVat(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcSponsorVat:", amount);
    return formatCash(0);
  }

  const professionalFee = amount;
  const platformFee = calcPlatformFee(amount); // 15% of amount
  const vat = 0.15 * (professionalFee + platformFee);

  return vat;
}

export function calcTotalPayableBySponsor(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcTotalPayableBySponsor:", amount);
    return formatCash(0);
  }

  const professionalFee = amount;
  const sponsorFee = calcSponsorServiceFee(amount); // 15% of amount
  const vat = calcSponsorVat(amount); // 15% of (amount + sponsor fee)

  return professionalFee + sponsorFee + vat;
}

export const financialCalculations = (TotalDeliverableCost, Brief) => {
  const CredFee10 = TotalDeliverableCost <= 2500 ? 250 : TotalDeliverableCost * 0.1; // Fixed fee of 250 if cost <= 2500, else 10%
  let voucherAmntDebited = 0;

  // Check if a sponsor amount exists in the brief and calculate debited voucher amount
  if (Brief?.tempVoucherObj?.sponsorAmount) {
    const vouchrAmnt = Brief.tempVoucherObj.sponsorAmount;
    voucherAmntDebited = Math.min(vouchrAmnt, TotalDeliverableCost); // Debit up to the total cost
  }

  const CredFee15 = voucherAmntDebited * 0.15; // 15% fee on debited voucher amount
  const TotAmtPay2Prof = TotalDeliverableCost - CredFee10; // Amount payable to professional
  const SponsorSubTot = CredFee15 + voucherAmntDebited; // Sponsor's subtotal
  const Remaining2BPayByClient = TotalDeliverableCost - voucherAmntDebited; // Remaining amount for client
  const VoucherRemaininBal = Brief?.tempVoucherObj?.sponsorAmount - voucherAmntDebited; // Remaining voucher balance
  const ClientSubTot = Remaining2BPayByClient * (TotalDeliverableCost <= 2500 ? 1 : 1.1); // Client's subtotal with adjusted fee

  // Return all calculated values as an object
  return {
    CredFee10,
    voucherAmntDebited,
    CredFee15,
    TotAmtPay2Prof,
    SponsorSubTot,
    Remaining2BPayByClient,
    VoucherRemaininBal,
    ClientSubTot,
  };
};

function isMatchingProfessional(professionalId, selectedPro) {
  return professionalId === selectedPro.id || professionalId === selectedPro._id;
}

export const processQuote = (Brief, SelectedPro) => {
  // Return default values if quote or professional ID is missing

  if (!Brief?.quote) return {
    totalPayable: 0,
    validBillables: []
  };
  // Filter valid billables for the selected professional
  const validBillables = Brief.quote
    .filter(q => isMatchingProfessional(q?.professional, SelectedPro)) // Match professional ID
    .flatMap(q => q.billables || []) // Extract billables
    .filter(Boolean); // Remove falsy values

  // Calculate total payable amount by summing up unit prices multiplied by quantities of valid billables
  const totalPayable = validBillables.reduce((sum, b) => {
    const quantity = b.quantity || 1; // Default quantity to 1 if not provided
    return sum + (b.unitPrice || 0) * quantity;
  }, 0);

  // Return total payable amount and valid billables
  return {
    totalPayable,
    validBillables
  };
};

export const processProfessionalsData = (pros, validProjects, validBriefs) => {
  // Map through professionals and enrich their data with related projects, briefs, and evaluations
  let professionals = pros.map(pro => {
    const userProjects = validProjects.filter(project => {
      if (Array.isArray(project.projectPlan)) {
        return project.projectPlan.some(plan => plan.professional === pro._id); // Check if any plan matches the professional
      }
      return project.projectPlan?.professional === pro._id; // Handle non-array case
    });
    const userBriefs = validBriefs.filter(job => {
      const professional = job.projectPlan[0]?.professional;
      return (typeof professional === 'object' ? professional._id : professional) === pro._id; // Handle both object and string cases
    });
    const evaluationsPerUser = validProjects
      .filter(project => {
        if (Array.isArray(project.projectPlan)) {
          return project.projectPlan.some(plan => plan.professional === pro._id); // Check if any plan matches the professional
        }
        return project.projectPlan?.professional === pro._id; // Handle non-array case
      })
      .map(project => project.evaluation);

    return {
      ...pro,
      projects: userProjects, // Add related projects
      briefs: userBriefs, // Add related briefs
      evaluations: evaluationsPerUser, // Add related evaluations
      jobCount: userProjects.length // Calculate job count as the sum of briefs and projects
    };
  });

  // Calculate additional metrics for each professional
  professionals.forEach(pro => {
    let totalRating = 0, ratingCount = 0, totalAmount = 0, briefAmount = 0, projectAmount = 0;

    // Calculate total rating and count of evaluations
    pro.evaluations?.forEach(evaluation => {
      const rating = evaluation?.professionalEvaluation?.summary?.credipple_rating;
      if (rating !== null && rating !== undefined) {
        totalRating += rating;
        ratingCount++;
      }
    });

    // Calculate total project budget estimation
    pro.projects.forEach(project => {
      if (project.budgetEstimation) {
        projectAmount += project.budgetEstimation;
      }
    });

    // Calculate total brief budget estimation
    pro.briefs.forEach(brief => {
      if (brief.budgetEstimation) {
        briefAmount += brief.budgetEstimation;
      }
    });

    // Calculate total amount and average rating
    totalAmount = projectAmount + briefAmount;
    pro.totalAmount = formatCash(totalAmount); // Format total amount as cash
    pro.averageRating = ratingCount > 0 ? totalRating / ratingCount : 'Not Assigned'; // Calculate average rating
  });

  // Sort professionals alphabetically by their full name
  return professionals.sort((a, b) => {
    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

export const processProjectsData = (confirmedProjects, projPlans, projects, evaluation, vouchers, quotes, prosAndLeads) => {
  if (!Array.isArray(confirmedProjects) || confirmedProjects.length === 0) return [];

  const briefs = confirmedProjects.filter(item => Array.isArray(item.projectPlan));
  briefs.forEach(brief => {
    enrichBriefWithData(brief, projPlans, prosAndLeads, evaluation, projects, vouchers, quotes);
  });

  const validBriefs = briefs.filter(
    item => Array.isArray(item.projectPlan) && item.projectPlan.length > 0 && typeof item.projectPlan[0] === 'object'
  );

  return validBriefs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

export const fetchProsAndLeads = async (pros) => {
  try {
    if (!Array.isArray(pros)) pros = [];

    const professionals = [];
    const leads = [];
    const clients = [];

    pros = pros.filter(item => item.roles?.length > 0);
    pros.forEach(pro => {
      if (pro.roles.includes(1988)) professionals.push(pro);
      else if (pro.roles.includes(1987)) leads.push(pro);
      else if (pro.roles.includes(1989)) clients.push(pro);
    });

    return { Leads: leads, Pros: professionals, Clients: clients };
  } catch (e) {
    console.error('Error fetching professionals and leads:', e);
    return { Leads: [], Pros: [], Clients: [] };
  }
};

export const getUserJobs = (Users, validProjects, validBriefs) => {
  return Users.map(user => {
    let userProjects = [];
    let userBriefs = [];

    if (user.roles[0] === 1989) {
      // Filter using client._id
      userProjects = validProjects.filter((project) => {
        if (project.client._id === user._id) {
          return project.client?._id === user._id
        }
      });
      userBriefs = validBriefs.filter(brief => brief.client?._id === user._id);
    } else if (user.roles[0] === 1987) {
      // Filter using talentPartner._id
      userProjects = validProjects.filter(project => project.talentPartner?._id === user._id);
      userBriefs = validBriefs.filter(brief => brief.talentPartner?._id === user._id);
    } else {
      // Check projectPlan for professional
      userBriefs = validBriefs.filter(brief => {
        if (Array.isArray(brief.projectPlan) && brief.projectPlan[0]?.professional) {
          return brief.projectPlan[0].professional === user._id;
        }
        return false;
      });

      userProjects = validProjects.filter(project => {
        if (Array.isArray(project.projectPlan)) {
          return project.projectPlan.some(plan => plan.professional === user._id);
        } else if (project.projectPlan?.professional) {
          return project.projectPlan.professional === user._id;
        }
        return false;
      });
    }

    return {
      ...user,
      projects: userProjects ?? [],
      briefs: userBriefs ?? [],
      jobCount: (userProjects.length || 0)
    };
  });
};

export const prepareBriefForUpdate = (brief) => {
  // Remove unnecessary properties
  delete brief.evaluation;
  delete brief.project;

  // Handle client property
  if (brief.client && typeof brief.client === 'object') {
    delete brief.client.briefs;
    delete brief.client.projects;
    brief.client = brief.client._id;
  } else {
    delete brief.client;
  }

  // Handle talentPartner property
  if (brief.talentPartner && typeof brief.talentPartner === 'object') {
    delete brief.talentPartner.briefs;
    delete brief.talentPartner.projects;
    brief.talentPartner = brief.talentPartner._id;
  } else {
    brief.talentPartner = '';
    delete brief.talentPartner;
  }

  // Handle professional in projectPlan
  if (brief.projectPlan?.[0]?.professional && typeof brief.projectPlan[0].professional === 'object') {
    brief.projectPlan[0].professional = brief.projectPlan[0].professional._id;
  }

  // Handle quote array
  if (Array.isArray(brief.quote)) {
    brief.quote = brief.quote.map(item => (item && typeof item === 'object' ? item._id : item));
  }

  // Reset serviceCategory
  brief.serviceCategory = '';

  return brief;
};

export const processProjectsAndBriefs = (validProjects, validBriefs, leads, evaluation, clients, projectPlans) => {
  // Process valid projects by enriching them with related brief, lead, evaluation, and other details
  let processedProjects = validProjects.map(project => {
    // Find the corresponding brief for the project
    const brief = validBriefs.find(b => b._id === project.brief || b._id === project.briefId);

    // Find the fulfillment lead for the project or assign default values if not found
    const lead = leads.find(l => l._id === project.fulfillmentLead || l._id === project.fullfilmentLead) || {
      firstName: 'Not Assigned',
      lastName: '',
      fullName: 'Not Assigned'
    };

    // If a brief is found, enrich the project with additional details
    if (brief !== undefined) {
      return {
        ...project,
        brief, // Add the related brief
        budgetEstimation: brief?.budgetEstimation || 0, // Use brief's budget estimation
        paymentMethod: brief?.paymentMethod || '', // Use brief's payment method
        voucherNumber: brief?.voucherNumber || '', // Use brief's voucher number
        description: brief?.briefDescription || '', // Use brief's description
        briefStatus: project.status || 'Not Started', // Use project status or default to 'Not Started'
        quote: brief?.quote || [], // Add quotes from the brief
        meetings: brief?.meetings || [], // Add meetings from the brief
        shortlist: brief?.shortlist || [], // Add shortlist from the brief
        scheduledCallNotes: brief?.scheduledCallNotes || [], // Add scheduled call notes from the brief
        scheduledCalls: brief?.scheduledCalls || [], // Add scheduled calls from the brief
        evaluation: evaluation.find(e => e._id === project.evaluation), // Add evaluation for the project
        fulfillmentLead: lead, // Add the fulfillment lead
        skillsOfProfessional: brief?.skillsOfProfessional || [], // Add skills of professional from the brief
        quote: brief.quote, // Initialize quote as an empty array
        client: clients.find(c => c._id === project.client), // Add the client for the project
        projectPlan: brief.projectPlan ?? [{
          // Add project plan or default to current date
          projEndDate: new Date().toISOString().split('T')[0],
          projStartDate: new Date().toDateString().split('T')[0],
        }]
      };
    }
  });

  // Filter out undefined projects (those without a corresponding brief)
  processedProjects = processedProjects.filter(item => item !== undefined);

  // Sort processed projects by createdAt in descending order
  processedProjects.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Update valid briefs by ensuring project plans are properly populated
  const updatedValidBriefs = validBriefs.map((item) => {
    const newItem = { ...item };

    // Handle cases where projectPlan is empty or null
    if (Array.isArray(newItem.projectPlan) && newItem.projectPlan.length === 0) {
      newItem.projectPlan = '';
    }
    if (newItem.projectPlan === null) {
      newItem.projectPlan = '';
    }

    // Enrich projectPlan with related project plan details
    if (Array.isArray(item.projectPlan) && typeof item.projectPlan[0] === 'object' && '_id' in item.projectPlan[0]) {
      item.projectPlan.push(projectPlans.find(p => p._id === item.projectPlan[0]._id));
    }
    newItem.projectPlan = [projectPlans.find(p => p._id === newItem.projectPlan[0])];

    return newItem;
  });

  // Sort updated briefs by createdAt in descending order
  updatedValidBriefs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Return the processed projects and updated briefs
  return { processedProjects, updatedValidBriefs };
};

//#region helpers
export const enrichBriefWithData = (brief, projPlans, prosAndLeads, evaluation, projects, vouchers, quotes) => {
  brief.projectPlan = projPlans.filter(plan => plan._id === brief.projectPlan[0]) || brief.projectPlan[0];
  brief.talentPartner = prosAndLeads.Leads.find(lead => lead._id === brief.talentPartner) || brief.talentPartner;
  brief.client = prosAndLeads.Clients.find(client => client._id === brief.client) || brief.client;

  if (Array.isArray(brief.projectPlan) && brief.projectPlan.length > 0) {
    const professional = brief.projectPlan[0]?.professional;
    if (typeof professional === 'object' && professional._id) {
      brief.projectPlan[0].professional = prosAndLeads.Pros.find(pro => pro._id === professional._id) || professional;
    } else {
      brief.projectPlan[0].professional = prosAndLeads.Pros.find(pro => pro._id === professional) || professional || '';
    }
  }

  const filteredProjects = projects.filter(item => item.brief !== undefined);
  const project = filteredProjects.find(item => item.brief === brief._id);

  if (project) {
    brief.evaluation = project.evaluation;
    brief.project = project;
  }

  if (Array.isArray(brief.quote)) {
    brief.quote = brief.quote.map(quoteId => quotes.find(quote => quote._id === quoteId) || '');
  } else {
    brief.quote = [];
  }

  if (Array.isArray(brief.shortlist)) {
    brief.shortlist.forEach(shortlistItem => {
      const professionalId = shortlistItem?.professional;
      shortlistItem.professional = prosAndLeads.Pros.find(pro => pro._id === professionalId) || professionalId || '';
    });
  } else {
    brief.shortlist = [];
  }

  brief.tempVoucherObj = vouchers.find(voucher => voucher.voucherNumber === brief.voucherNumber) || '';
  brief.serviceCategory = brief.typeOfProfessional ?? [];
};

//
export const truncateText = (text, limit) => {
  if (text.length <= limit) return text;
  return text.substring(0, limit) + '...';
};
